import { getSharedPdfWorker, loadPdfjs } from "./pdfjs"

const STORAGE_KEY = "pdfPageCounts"

/**
 * Attachment URLs are presigned, so the query string changes on every fetch
 * while the file behind it stays the same. Keying on the path alone keeps the
 * cache usable across reloads.
 */
const cacheKey = (url: string): string => {
  try {
    const { origin, pathname } = new URL(url, window.location.origin)
    return `${origin}${pathname}`
  } catch {
    return url
  }
}

/**
 * Page counts never change for a given file, so an unbounded cache would only
 * grow. We keep the most recent entries and drop the oldest.
 */
const MAX_ENTRIES = 500

let counts: Map<string, number> | null = null

const readStorage = (): Map<string, number> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return new Map()
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return new Map()
    }
    const entries = Object.entries(parsed as Record<string, unknown>).filter(
      (entry): entry is [string, number] =>
        typeof entry[1] === "number" && Number.isFinite(entry[1]),
    )
    return new Map(entries)
  } catch {
    return new Map()
  }
}

const getCounts = (): Map<string, number> => {
  if (!counts) counts = readStorage()
  return counts
}

const writeStorage = (map: Map<string, number>) => {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(Object.fromEntries(map)),
    )
  } catch {
    // A full or unavailable localStorage must not break the page count.
  }
}

const remember = (key: string, pageCount: number) => {
  const map = getCounts()
  // Re-inserting moves the key to the end, so the oldest entry is dropped first.
  map.delete(key)
  map.set(key, pageCount)
  while (map.size > MAX_ENTRIES) {
    const oldest = map.keys().next()
    if (oldest.done) break
    map.delete(oldest.value)
  }
  writeStorage(map)
}

/** Page counts already known, without touching the network. */
export const getCachedPageCount = (url: string): number | null =>
  getCounts().get(cacheKey(url)) ?? null

/**
 * Parses in flight, keyed the same way as the cache. Two messages pointing at
 * the same file — or the same message remounting while the first parse is
 * still running — share one parse instead of starting another.
 */
const inFlight = new Map<string, Promise<number | null>>()

const parsePageCount = async (url: string): Promise<number | null> => {
  const [lib, worker] = await Promise.all([loadPdfjs(), getSharedPdfWorker()])
  const task = lib.getDocument({ url, worker })

  try {
    const doc = await task.promise
    const { numPages } = doc
    // Releases the document's own resources; the shared worker stays alive.
    await doc.destroy()
    return numPages
  } catch {
    // The page count is decoration - a broken or unreachable file is not worth
    // reporting, and the caller simply renders no count.
    return null
  }
}

/**
 * Resolves the page count for a PDF, reading from cache when possible.
 *
 * Only the first call for a file does real work. Later calls - a different
 * message with the same attachment, or the user returning to a chat - are
 * answered from memory or localStorage.
 */
export const resolvePageCount = (url: string): Promise<number | null> => {
  const key = cacheKey(url)

  const cached = getCounts().get(key)
  if (cached !== undefined) return Promise.resolve(cached)

  const pending = inFlight.get(key)
  if (pending) return pending

  const request = parsePageCount(url)
    .then((pageCount) => {
      if (pageCount !== null) remember(key, pageCount)
      return pageCount
    })
    .finally(() => {
      inFlight.delete(key)
    })

  inFlight.set(key, request)
  return request
}

/** Test hook: clears the in-memory cache and any parse in flight. */
export const resetPageCountCacheForTests = () => {
  counts = null
  inFlight.clear()
}
